import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { 
  PageContainer, 
  BrandingSection, 
  FormSection, 
  FormCard, 
  StyledForm, 
  StyledTitle, 
  StyledButton,
  LogoContainer,
  BrandingContent,
  FeatureList,
  FeatureItem,
  ThemeToggle
} from './StyledComponents';

// Additional styled components for OTP
const OTPInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 2rem 0;
`;

const OTPInput = styled.input`
  width: 3rem;
  height: 3rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: bold;
  border: 2px solid ${props => props.darkMode ? '#475569' : '#e5e7eb'};
  border-radius: 12px;
  background-color: ${props => props.darkMode ? '#334155' : '#f9fafb'};
  color: ${props => props.darkMode ? '#f1f5f9' : '#1e293b'};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.darkMode ? '#64748b' : '#5d32b0'};
    box-shadow: ${props =>
      props.darkMode
        ? '0 0 0 3px rgba(100, 116, 139, 0.35)'
        : '0 0 0 3px rgba(93, 50, 176, 0.2)'};
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.darkMode ? '#a9d045' : '#5d32b0'};
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: none;
  padding: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const InfoBox = styled.div`
  background-color: ${props => props.darkMode ? 'rgba(169, 208, 69, 0.05)' : 'rgba(93, 50, 176, 0.05)'};
  border: 1px solid ${props => props.darkMode ? 'rgba(169, 208, 69, 0.14)' : 'rgba(93, 50, 176, 0.14)'};
  border-radius: 0.625rem;
  padding: 1rem;
  margin: 1rem 0;
  font-size: 0.875rem;
  color: ${props => props.darkMode ? '#cbd5e1' : '#52525b'};
`;

const EmailText = styled.p`
  color: ${props => props.darkMode ? '#a9d045' : '#5d32b0'};
  font-weight: 600;
  margin-top: 0.5rem;
`;

const OTPVerification = ({ userData, onBack }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [deliveryMethod, setDeliveryMethod] = useState(localStorage.getItem('lastOTPDeliveryMethod') || 'email');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('authDarkMode');
    return saved ? JSON.parse(saved) : false;
  });
  
  const { verifyOTPAndRegister, resendOTP } = useAuth();
  const navigate = useNavigate();

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('authDarkMode', JSON.stringify(newMode));
  };

  // Countdown timer for resend button
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      // Focus last input
      const lastInput = document.getElementById('otp-5');
      if (lastInput) lastInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOTPAndRegister({
        ...userData,
        otp: otpString
      });
      
      if (result.success) {
        toast.success('Account created successfully!');
        navigate('/user/dashboard', { replace: true });
      } else {
        toast.error(result.message);
        // Clear OTP on error
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0').focus();
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async (method = deliveryMethod) => {
    if (countdown > 0) return;
    
    setResendLoading(true);
    try {
      const result = await resendOTP(
        userData.email, 
        userData.phoneNumber || null, 
        method
      );
      if (result.success) {
        setDeliveryMethod(method);
        localStorage.setItem('lastOTPDeliveryMethod', method);
        toast.success(`OTP resent via ${method === 'email' ? 'email' : 'WhatsApp'}`);
        setCountdown(60); // 60 seconds countdown
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0').focus();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <PageContainer darkMode={darkMode}>
      <ThemeToggle onClick={toggleDarkMode} darkMode={darkMode} />
      <BrandingSection darkMode={darkMode}>
        <LogoContainer>
          <img 
            src="/skyone-logo-no-bg.png" 
            alt="SkyOne" 
          />
        </LogoContainer>
        <BrandingContent darkMode={darkMode}>
          <h1>Verify Your Account</h1>
          <p>We've sent a 6-digit verification code {deliveryMethod === 'email' ? 'to your email address' : 'via WhatsApp'}</p>
          <EmailText darkMode={darkMode}>
            {deliveryMethod === 'email' ? userData.email : userData.phoneNumber}
          </EmailText>
          <FeatureList>
            <FeatureItem darkMode={darkMode}>
              <div className="icon">
                <svg className={`h-6 w-6 ${darkMode ? "text-[#a9d045]" : "text-[#5d32b0]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="text">
                <div className="title">Secure Verification</div>
                <div className="description">Code expires in 10 minutes</div>
              </div>
            </FeatureItem>
            <FeatureItem darkMode={darkMode}>
              <div className="icon">
                <svg className={`h-6 w-6 ${darkMode ? "text-[#a9d045]" : "text-[#5d32b0]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text">
                <div className="title">3 Attempts</div>
                <div className="description">Maximum verification attempts</div>
              </div>
            </FeatureItem>
            <FeatureItem darkMode={darkMode}>
              <div className="icon">
                <svg className={`h-6 w-6 ${darkMode ? "text-[#a9d045]" : "text-[#5d32b0]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text">
                <div className="title">Check Inbox</div>
                <div className="description">Don't forget your spam folder</div>
              </div>
            </FeatureItem>
          </FeatureList>
        </BrandingContent>
      </BrandingSection>

      <FormSection darkMode={darkMode}>
        <FormCard darkMode={darkMode}>
          <StyledForm onSubmit={handleSubmit}>
            <StyledTitle darkMode={darkMode}>Enter Verification Code</StyledTitle>
            
            <OTPInputContainer>
              {otp.map((digit, index) => (
                <OTPInput
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  darkMode={darkMode}
                  autoComplete="off"
                  disabled={loading}
                />
              ))}
            </OTPInputContainer>

            <StyledButton $darkMode={darkMode} type="submit" disabled={loading || otp.join('').length !== 6}>
              {loading ? 'Verifying...' : 'Verify & Create Account'}
            </StyledButton>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <LinkButton 
                type="button"
                onClick={() => handleResendOTP(deliveryMethod)}
                disabled={resendLoading || countdown > 0}
                darkMode={darkMode}
              >
                {resendLoading 
                  ? 'Sending...' 
                  : countdown > 0 
                  ? `Resend in ${countdown}s` 
                  : `Resend via ${deliveryMethod === 'email' ? 'Email' : 'WhatsApp'}`
                }
              </LinkButton>
              {userData.phoneNumber && (
                <div style={{ marginTop: '0.5rem' }}>
                  <LinkButton 
                    type="button"
                    onClick={() => handleResendOTP(deliveryMethod === 'email' ? 'whatsapp' : 'email')}
                    disabled={resendLoading || countdown > 0}
                    darkMode={darkMode}
                    style={{ fontSize: '0.75rem' }}
                  >
                    Or send via {deliveryMethod === 'email' ? 'WhatsApp' : 'Email'}
                  </LinkButton>
                </div>
              )}
            </div>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <LinkButton 
                type="button"
                onClick={onBack}
                darkMode={darkMode}
              >
                ← Back to registration
              </LinkButton>
            </div>

            <InfoBox darkMode={darkMode}>
              <strong>Important:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', listStyle: 'disc' }}>
                {deliveryMethod === 'email' && <li>Check your spam folder if you don't see the email</li>}
                {deliveryMethod === 'whatsapp' && <li>Check your WhatsApp messages</li>}
                <li>The code expires in 10 minutes</li>
                <li>You have 3 attempts to enter the correct code</li>
              </ul>
            </InfoBox>
          </StyledForm>
        </FormCard>
      </FormSection>
    </PageContainer>
  );
};

export default OTPVerification;
